/**
 * Job Posting Guard — Royalty+ tier per product matrix (admin bypass).
 * Mirrors ProfessionalProfileGuard. Tier from subscription sync is the
 * entitlement; the service layer also re-checks at save time.
 */

import React, { ReactNode, useEffect } from 'react';
import { useFirebaseAuth } from '@/hooks/use-firebase-auth';
import { useSubscription } from '@/hooks/use-subscription';
import { hasRole, normalizeRoles } from '@/lib/normalize-user-data';
import { hasFeatureAccess, FeatureAccess } from '@/lib/subscription-feature-guards';
import { UpgradeModal } from '@/components/modals/UpgradeModal';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';

interface JobPostingGuardProps {
  children: ReactNode;
}

export function JobPostingGuard({ children }: JobPostingGuardProps) {
  const { user, isLoading } = useFirebaseAuth();
  const {
    subscriptionTier,
    subscriptionStatus,
    planName,
    loading: subscriptionLoading,
  } = useSubscription();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (!isLoading && !user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in or create an account to post a job.',
        variant: 'destructive',
      });
      navigate(`/auth?returnTo=${encodeURIComponent('/jobs/create')}`);
    }
  }, [isLoading, user, navigate, toast]);

  if (isLoading || !user) return null;
  if (subscriptionLoading) return <>{children}</>;

  const isAdmin = hasRole(user, 'ADMIN');
  // Job posting requires CONTACT_BOOK_PROFESSIONALS — Royalty+ per the matrix.
  const hasJobPostingTier = hasFeatureAccess(
    subscriptionTier,
    FeatureAccess.CONTACT_BOOK_PROFESSIONALS,
    { subscriptionStatus }
  );

  if (isAdmin || hasJobPostingTier) {
    return <>{children}</>;
  }

  const normalizedRoles = normalizeRoles(user);
  return (
    <UpgradeModal
      isOpen={true}
      onClose={() => window.history.back()}
      onUpgrade={() => {}}
      requiredTier="Royalty"
      featureName="Post a Job"
      userTier={planName || 'Free'}
      userRoles={normalizedRoles}
      rawUserData={{
        subscriptionTier: subscriptionTier || undefined,
        roles: user?.roles,
        email: user?.email,
      }}
    />
  );
}
